const getObjectPropCI = (obj, ...names) => {
  if (!obj || typeof obj !== "object") return undefined;
  const keyMap = new Map(Object.keys(obj).map((key) => [String(key).toLowerCase(), key]));
  for (const name of names) {
    const actualKey = keyMap.get(String(name).toLowerCase());
    if (actualKey !== undefined) return obj[actualKey];
  }
  return undefined;
};

const parseMaybeJsonValue = (value) => {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!trimmed) return value;
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
};

export const CHAT_OUTPUT_KIND_MESSAGE = "Message";
export const CHAT_OUTPUT_KIND_RENDER = "Render";
export const CHAT_OUTPUT_KIND_TABLE = "Table";
export const CHAT_OUTPUT_KIND_CHART = "Chart";
export const CHAT_OUTPUT_KIND_PREVIEW = "Preview";
export const CHAT_OUTPUT_KIND_PLAN = "Plan";

export const CHAT_REVIEW_STATE_NONE = "None";
export const CHAT_REVIEW_STATE_PENDING = "Pending";
export const CHAT_REVIEW_STATE_ACCEPTED = "Accepted";
export const CHAT_REVIEW_STATE_REJECTED = "Rejected";

export const normalizeChatUiCommand = (value) => {
  const normalized = String(value ?? "").trim().toLowerCase();
  if (normalized === "display") return "data";
  return normalized;
};

export const normalizeChatOutputKind = (value) => {
  const normalized = String(value ?? "").trim().toLowerCase();
  switch (normalized) {
    case "render":
      return CHAT_OUTPUT_KIND_RENDER;
    case "table":
      return CHAT_OUTPUT_KIND_TABLE;
    case "chart":
      return CHAT_OUTPUT_KIND_CHART;
    case "preview":
      return CHAT_OUTPUT_KIND_PREVIEW;
    case "plan":
      return CHAT_OUTPUT_KIND_PLAN;
    default:
      return CHAT_OUTPUT_KIND_MESSAGE;
  }
};

export const normalizeChatReviewState = (value) => {
  const normalized = String(value ?? "").trim().toLowerCase();
  switch (normalized) {
    case "pending":
      return CHAT_REVIEW_STATE_PENDING;
    case "accepted":
      return CHAT_REVIEW_STATE_ACCEPTED;
    case "rejected":
      return CHAT_REVIEW_STATE_REJECTED;
    default:
      return CHAT_REVIEW_STATE_NONE;
  }
};

const resolveEmbeddedUiCommand = (chatItem) => {
  const candidates = [
    chatItem?.Output,
    chatItem?.output,
    chatItem?.Value,
    chatItem?.value,
    chatItem?.ModelResponse,
    chatItem?.modelResponse,
    chatItem?.ResponseMessage,
    chatItem?.responseMessage
  ]
    .map((entry) => parseMaybeJsonValue(entry))
    .filter((entry) => entry && typeof entry === "object");

  for (const candidate of candidates) {
    const direct = getObjectPropCI(candidate, "uiCommand", "UICommand", "UiCommand");
    if (String(direct ?? "").trim()) {
      return String(direct).trim();
    }

    const args = getObjectPropCI(candidate, "args", "Args");
    const fromArgs = getObjectPropCI(args, "uiCommand", "UICommand", "UiCommand");
    if (String(fromArgs ?? "").trim()) {
      return String(fromArgs).trim();
    }
  }

  return "";
};

export const resolveChatOutputKind = (chatItem, fallbackUiCommand = null) => {
  const persistedOutputKind = getObjectPropCI(chatItem, "OutputKind", "outputKind");
  if (String(persistedOutputKind ?? "").trim()) {
    return normalizeChatOutputKind(persistedOutputKind);
  }

  const directUiCommand =
    getObjectPropCI(chatItem, "UICommand", "uiCommand", "UiCommand") ??
    fallbackUiCommand ??
    resolveEmbeddedUiCommand(chatItem);
  const normalizedUiCommand = normalizeChatUiCommand(directUiCommand);

  switch (normalizedUiCommand) {
    case "render":
      return CHAT_OUTPUT_KIND_RENDER;
    case "data":
      return CHAT_OUTPUT_KIND_TABLE;
    case "chart":
      return CHAT_OUTPUT_KIND_CHART;
    case "preview":
      return CHAT_OUTPUT_KIND_PREVIEW;
    case "plan":
      return CHAT_OUTPUT_KIND_PLAN;
    default:
      return CHAT_OUTPUT_KIND_MESSAGE;
  }
};

export const resolveChatReviewState = (chatItem, resolvedOutputKind = null) => {
  const persistedReviewState = getObjectPropCI(chatItem, "ReviewState", "reviewState");
  if (String(persistedReviewState ?? "").trim()) {
    return normalizeChatReviewState(persistedReviewState);
  }

  const outputKind = normalizeChatOutputKind(resolvedOutputKind ?? resolveChatOutputKind(chatItem));
  if (outputKind === CHAT_OUTPUT_KIND_PREVIEW || outputKind === CHAT_OUTPUT_KIND_PLAN) {
    return CHAT_REVIEW_STATE_PENDING;
  }

  return CHAT_REVIEW_STATE_NONE;
};

export const resolveChatHistoryDisplayMode = (chatItem, fallbackUiCommand = null) => {
  const outputKind = resolveChatOutputKind(chatItem, fallbackUiCommand);
  switch (outputKind) {
    case CHAT_OUTPUT_KIND_RENDER:
      return "render";
    case CHAT_OUTPUT_KIND_TABLE:
      return "data";
    case CHAT_OUTPUT_KIND_CHART:
      return "chart";
    default:
      return null;
  }
};

export const isPendingReviewChatItem = (chatItem, expectedOutputKind = null) => {
  const outputKind = resolveChatOutputKind(chatItem);
  if (
    expectedOutputKind &&
    normalizeChatOutputKind(expectedOutputKind) !== outputKind
  ) {
    return false;
  }

  if (outputKind !== CHAT_OUTPUT_KIND_PREVIEW && outputKind !== CHAT_OUTPUT_KIND_PLAN) {
    return false;
  }

  return resolveChatReviewState(chatItem, outputKind) === CHAT_REVIEW_STATE_PENDING;
};
